import { Alert, AlertTitle, Button, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';

interface ErrorAlertProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}

const ErrorAlert: React.FC<ErrorAlertProps> = ({
  error,
  onRetry,
  title = 'Si è verificato un errore'
}) => {
  // Extract error message
  const getErrorMessage = (err: unknown): string => {
    if (!err) return 'Errore sconosciuto';

    // Check for axios error
    if (typeof err === 'object' && err !== null) {
      const axiosError = err as any;

      // Network error
      if (axiosError.message === 'Network Error' || !axiosError.response) {
        return 'Errore di connessione. Verifica la tua connessione a Internet e riprova.';
      }

      // Server error with message
      if (axiosError.response?.data?.message) {
        return axiosError.response.data.message;
      }

      // HTTP status error
      if (axiosError.response?.status) {
        const status = axiosError.response.status;
        if (status >= 500) {
          return 'Il server ha riscontrato un errore. Riprova tra qualche istante.';
        }
        if (status === 429) {
          return 'Troppe richieste. Attendi qualche secondo e riprova.';
        }
        if (status === 403) {
          return 'Non hai i permessi necessari per eseguire questa operazione.';
        }
        if (status === 404) {
          return 'Risorsa non trovata.';
        }
      }

      // Generic axios error message
      if (axiosError.message) {
        return axiosError.message;
      }
    }

    // String error
    if (typeof err === 'string') {
      return err;
    }

    // Error object with message
    if (err instanceof Error) {
      return err.message;
    }

    return 'Si è verificato un errore imprevisto';
  };

  const errorMessage = getErrorMessage(error);

  return (
    <Alert
      severity="error"
      role="alert"
      aria-live="assertive"
      sx={{ mb: 3 }}
      action={
        onRetry && (
          <Button
            color="inherit"
            size="small"
            startIcon={<Refresh />}
            onClick={onRetry}
            aria-label="Riprova operazione"
          >
            Riprova
          </Button>
        )
      }
    >
      <AlertTitle>{title}</AlertTitle>
      <Box component="span" sx={{ display: 'block' }}>
        {errorMessage}
      </Box>
    </Alert>
  );
};

export default ErrorAlert;
